import React, { type ReactNode, useCallback } from 'react';

import { ErrorBoundary } from 'react-error-boundary';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import {
	PopupTriggerWithHover,
	type PopupTriggerWithHoverProps,
} from '@atlaskit/teams-app-internal-popup-adaptor/popup-trigger-with-hover';
import { layers } from '@atlaskit/theme/constants';
import {
	agentProfileCardEntryPoint,
	type AgentProfileCardEntryPointParams,
} from '@atlassian/conversation-assistant-agent/common/ui/agent-profile-card-entrypoint';
import type { AgentProfileCardRuntimeProps } from '@atlassian/conversation-assistant-agent/common/ui/agent-profile-card-types';
import { AgentProfileCardSkeleton } from '@atlassian/conversation-assistant-agent/common/ui/AgentProfileCardSkeleton';

export type AgentProfileCardWithTriggerProps = AgentProfileCardEntryPointParams &
	AgentProfileCardRuntimeProps &
	Pick<PopupTriggerWithHoverProps<typeof agentProfileCardEntryPoint>, 'placement'> & {
		/**
		 * How the popup should be triggered.
		 * Options: 'hover', 'click', or both.
		 */
		trigger?: PopupTriggerWithHoverProps<typeof agentProfileCardEntryPoint>['triggerMode'];
		/**
		 * Whether to render the popup in the parent DOM node instead of a portal.
		 */
		shouldRenderToParent?: boolean;
		/**
		 * Trigger element that opens the agent profile card on interaction.
		 *
		 * Used as the fallback when `renderTrigger` is not provided, or when the card cannot be
		 * rendered (no agent reference, or an error). Optional when `renderTrigger` supplies the
		 * trigger.
		 */
		children?: ReactNode;
		/**
		 * Render the trigger element with the popup's trigger props spread onto it.
		 *
		 * The trigger props are spread onto the consumer's already-interactive element instead of
		 * wrapping `children`, preserving the element's own interactive semantics and styling.
		 */
		renderTrigger?: (triggerProps: AgentProfileCardTriggerProps) => ReactNode;
	};

type AgentProfileCardTriggerProps = Parameters<
	NonNullable<PopupTriggerWithHoverProps<typeof agentProfileCardEntryPoint>['trigger']>
>[0];

/**
 * Displays an agent profile card in a popup when a trigger element is interacted with.
 * Loads the card content lazily via a Relay entrypoint.
 */
export function AgentProfileCardWithTrigger({
	children,
	renderTrigger,
	trigger,
	placement = 'bottom-start',
	shouldRenderToParent = false,
	cloudId,
	hideStarButton,
	hideConversationStarters,
	hideAiDisclaimer,
	onChatClick,
	onConversationStarterClick,
	onViewProfile,
	onDuplicate,
	onCopyLink,
	onEdit,
	onDelete,
	renderCTA,
	renderMoreActions,
	...entryPointParams
}: AgentProfileCardWithTriggerProps): React.JSX.Element {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const agentAri = 'agentAri' in entryPointParams ? entryPointParams.agentAri : undefined;
	const agentId = 'agentId' in entryPointParams ? entryPointParams.agentId : undefined;
	const identityAccountId =
		'identityAccountId' in entryPointParams ? entryPointParams.identityAccountId : undefined;
	const hasAgentRef = agentAri || agentId || identityAccountId;

	const onErrorBoundary = useCallback(
		(error: Error) => {
			createAnalyticsEvent({
				action: 'failed',
				actionSubject: 'agentProfileCardWithTrigger',
				eventType: 'operational',
				attributes: {
					source: 'agentProfileCard',
					errorName: error?.name,
					errorMessage: error?.message,
				},
			}).fire();
		},
		[createAnalyticsEvent],
	);

	const handlePopupOpen = useCallback(
		(method: 'hover' | 'click') => {
			createAnalyticsEvent({
				action: 'opened',
				actionSubject: 'agentProfileCardPopup',
				eventType: 'ui',
				attributes: {
					source: 'agentProfileCard',
					triggerMethod: method,
					agentAri,
					agentId,
					identityAccountId,
				},
			}).fire();
		},
		[createAnalyticsEvent, agentAri, agentId, identityAccountId],
	);

	const handlePopupClose = useCallback(() => {
		createAnalyticsEvent({
			action: 'closed',
			actionSubject: 'agentProfileCardPopup',
			eventType: 'ui',
			attributes: {
				source: 'agentProfileCard',
				agentAri,
				agentId,
				identityAccountId,
			},
		}).fire();
	}, [createAnalyticsEvent, agentAri, agentId, identityAccountId]);

	if (!entryPointParams || !cloudId || !hasAgentRef) {
		return <>{children}</>;
	}

	return (
		<ErrorBoundary onError={onErrorBoundary} fallback={<>{children}</>}>
			<PopupTriggerWithHover
				entryPoint={agentProfileCardEntryPoint}
				placement={placement}
				fallback={<AgentProfileCardSkeleton />}
				entryPointParams={{ ...entryPointParams, cloudId }}
				entryPointProps={{
					cloudId,
					identityAccountId,
					hideStarButton,
					hideConversationStarters,
					hideAiDisclaimer,
					onChatClick,
					onConversationStarterClick,
					onViewProfile,
					onDuplicate,
					onCopyLink,
					onEdit,
					onDelete,
					renderCTA,
					renderMoreActions,
				}}
				shouldRenderToParent={shouldRenderToParent}
				shouldUseCaptureOnOutsideClick={true}
				zIndex={layers.modal()}
				triggerMode={trigger}
				onOpen={handlePopupOpen}
				onClose={handlePopupClose}
				trigger={(triggerProps) =>
					renderTrigger ? renderTrigger(triggerProps) : <span {...triggerProps}>{children}</span>
				}
			/>
		</ErrorBoundary>
	);
}
